import { useEffect, useRef } from 'react';
import { supabase } from '@/api/supabase';
import { useNotifications } from '@/contexts/notification-context';

/**
 * Hook to listen for action queue updates and show notifications
 */
export function useActionNotifications() {
  const { addNotification, removeNotification, notifications } = useNotifications();
  // Keep track of pending notification IDs by device and datapoint
  const pendingNotifications = useRef<Record<string, string>>({});

  // Define durations for different notification types (in milliseconds)
  const NOTIFICATION_DURATIONS = {
    pending: 30000,      // 30 seconds for pending notifications
    processing: 120000,   // 2 minutes for processing notifications
    success: 5000,       // 5 seconds for success notifications
    failed: 8000         // 8 seconds for failed notifications
  };

  // Helper function to safely remove a notification and clean up tracking
  const safeRemoveNotification = (notificationKey: string) => {
    const notificationId = pendingNotifications.current[notificationKey];
    if (notificationId) {
      // Check if the notification still exists before trying to remove it
      const exists = notifications.some(n => n.id === notificationId);
      if (exists) {
        console.log('Removing existing notification:', notificationId);
        removeNotification(notificationId);
      } else {
        console.log('Notification already removed:', notificationId);
      }
      delete pendingNotifications.current[notificationKey];
    }
  };

  useEffect(() => {
    // Subscribe to action_queue table changes
    const subscription = supabase
      .channel('action_queue_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'action_queue',
        },
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;

          // Handle different event types
          if (eventType === 'INSERT') {
            // New action added
            // Only show notifications for manual command types
            if (newRecord.status === 'pending' && newRecord.command_type === 'manual') {
              // Create a unique key for this device/datapoint combination
              const notificationKey = `${newRecord.device_id}:${newRecord.datapoint}`;

              // Add the pending notification and store its ID
              const notificationId = addNotification({
                message: `Command Queued`,
                type: 'pending',
                deviceId: newRecord.device_id,
                datapoint: `${newRecord.datapoint} → ${newRecord.value}`,
                duration: NOTIFICATION_DURATIONS.pending
              });

              // Store the notification ID for later removal
              pendingNotifications.current[notificationKey] = notificationId;
            }
          } else if (eventType === 'UPDATE') {
            // Action status changed
            if (oldRecord.status !== newRecord.status) {
              // Create a unique key for this device/datapoint combination
              const notificationKey = `${newRecord.device_id}:${newRecord.datapoint}`;

              // Only show notifications for manual command types
              if (newRecord.status === 'processing' && newRecord.command_type === 'manual') {
                // Remove any pending notification for this device/datapoint
                safeRemoveNotification(notificationKey);

                // Add the processing notification and store its ID
                const processingNotificationId = addNotification({
                  message: `Processing Command`,
                  type: 'processing',
                  deviceId: newRecord.device_id,
                  datapoint: `${newRecord.datapoint} → ${newRecord.value}`,
                  duration: NOTIFICATION_DURATIONS.processing
                });

                // Store the notification ID for later removal
                pendingNotifications.current[notificationKey] = processingNotificationId;
              } else if ((newRecord.status === 'completed' || newRecord.status === 'ongoing') && newRecord.command_type === 'manual') {
                // Remove any pending or processing notification for this device/datapoint
                safeRemoveNotification(notificationKey);

                // For success notifications, we want them to stay visible longer
                // The NotificationProvider will auto-remove them after the configured timeout
                addNotification({
                  message: `Command Successful`,
                  type: 'success',
                  deviceId: newRecord.device_id,
                  datapoint: `${newRecord.datapoint} → ${newRecord.value}`,
                  duration: NOTIFICATION_DURATIONS.success
                });
              } else if (newRecord.status === 'failed' && newRecord.command_type === 'manual') {
                // Remove any pending or processing notification for this device/datapoint
                safeRemoveNotification(notificationKey);

                // For failure notifications, we want them to stay visible longer
                // The NotificationProvider will auto-remove them after the configured timeout
                addNotification({
                  message: `Command Failed`,
                  type: 'failed',
                  deviceId: newRecord.device_id,
                  datapoint: `${newRecord.datapoint} → ${newRecord.value}`,
                  duration: NOTIFICATION_DURATIONS.failed
                });
              }
            }
          } else if (eventType === 'DELETE') {
            // If the action was deleted and had a status of 'processing',
            // it might have failed or completed
            if (oldRecord.status === 'processing' && oldRecord.command_type === 'manual') {
              // Create a unique key for this device/datapoint combination
              const notificationKey = `${oldRecord.device_id}:${oldRecord.datapoint}`;

              // Remove any pending or processing notification for this device/datapoint
              safeRemoveNotification(notificationKey);

              // We don't know if it succeeded or failed, so we'll assume success
              // since that's the most common case when an action is deleted
              // For success notifications, we want them to stay visible longer
              addNotification({
                message: `Command Completed`,
                type: 'success',
                deviceId: oldRecord.device_id,
                datapoint: `${oldRecord.datapoint} → ${oldRecord.value}`,
                duration: NOTIFICATION_DURATIONS.success
              });
            }
          }
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [
    addNotification,
    removeNotification,
    // These are constants defined in the component, so they don't need to be dependencies
    // but we're including them to satisfy the linter
    NOTIFICATION_DURATIONS.pending,
    NOTIFICATION_DURATIONS.processing,
    NOTIFICATION_DURATIONS.success,
    NOTIFICATION_DURATIONS.failed
  ]);
}
